// Componente TripCardAddFile
// Este componente permite arrastar e soltar um arquivo CSV/Excel ou clicar para selecionar, exibindo o nome do arquivo selecionado.
import { formatStopTime } from "@utils";
import React, { useRef, useState } from "react";
import { EOperationType, ICsvData } from "src/screens/trips/types";
import * as XLSX from 'xlsx';

interface TripCardAddFileProps {
  icon: string;
  text: string;
  text2?: string;
  textBtnNext?: string;
  textBtnDownload?: string;
  onNext?: (json?: any[]) => void;
  onDownload?: () => void;
  isActive?: boolean;
}

const expectedColumnCount = 8;



// Função para gerar e baixar o XLS de exemplo
async function handleDownloadXlsExample() {
  const fileName = "Modelo_importacao_de_rotas_Rio_Routing.xlsx";
  const publicUrl = process.env.PUBLIC_URL || '';

  // Lista de possíveis caminhos para tentar
  const possiblePaths = [
    `${publicUrl}/files/modelo_importação_de_rotas_Rio_Routing.xlsx`,
    `${window.location.origin}/files/modelo_importação_de_rotas_Rio_Routing.xlsx`,
    `/files/modelo_importação_de_rotas_Rio_Routing.xlsx`,
    `./files/modelo_importação_de_rotas_Rio_Routing.xlsx`
  ];

  // Método 1: Tentar fetch com diferentes caminhos
  for (const fileUrl of possiblePaths) {
    try {
      const response = await fetch(fileUrl);

      if (response.ok) {
        const blob = await response.blob();
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Limpar o objeto URL após o download
        setTimeout(() => URL.revokeObjectURL(link.href), 100);
        return; // Sucesso, sair da função
      }
    } catch (error) {
      console.warn(`Falha ao baixar de ${fileUrl}:`, error);
      continue; // Tentar próximo caminho
    }
  }

  // Método 2: Fallback - abrir em nova aba
  try {
    const fallbackUrl = possiblePaths[0];
    window.open(fallbackUrl, '_blank');
  } catch (error) {
    console.error('Todos os métodos de download falharam:', error);
    alert('Não foi possível baixar o arquivo. Verifique se o arquivo existe na pasta public/files/');
  }
}

const TripCardAddFile = ({
  icon,
  onNext,
  onDownload,
  text,
  text2 = " ",
  textBtnNext,
  textBtnDownload,
  isActive = false,
}: TripCardAddFileProps) => {

  const [downloading, setDownloading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isValid, setIsValid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [csvJson, setCsvJson] = useState<ICsvData[] | null>(null);

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const isCoordinate = (value: string): boolean => {
    if (!value) return false;
    const coordinatePattern = /^-?\d+\.?\d*,\s*-?\d+\.?\d*$/;
    return coordinatePattern.test(value.trim());
  };

  const getNewRows = (rows: string[][]) => {
    return rows?.map((child) => child?.slice(0, 8))?.filter(
      row => row.length > 0 && !row?.every(value => value === null || value === undefined || value === '')
    );
  };

  const rowSlice = (lines: string[]) => {
    return lines.slice(1).map(line => line.split(';').map(cell => cell.trim()));
  }

  // Função para ler o arquivo CSV/XLS e converter para JSON
  const readFile = (file: File) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (fileExtension === 'xls' || fileExtension === 'xlsx' || fileExtension === 'csv') {
      // Leitura de arquivo Excel
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (jsonData.length < 2) {
            setIsValid(false);
            setError('Arquivo Excel vazio ou inválido.');
            setCsvJson(null);
            return;
          }

          const rows = jsonData.slice(1) as string[][];
          const newRows = getNewRows(rows);

          // Validação do número de colunas
          const isColumnCountValid = newRows.every(row => row.length >= expectedColumnCount);

          if (isColumnCountValid) {
            setIsValid(true);
            setError(null);
            const dataJsonXLSTemp: ICsvData[] = newRows.map(row => {
              // Lê pela sequência das colunas (índices fixos)
              const empresa = row[0] ? String(row[0]).trim() : '';
              const endereco = row[1] ? String(row[1]).trim() : '';
              const cep = row[2] ? String(row[2]).trim() : '';
              const cidade = row[3] ? String(row[3]).trim() : '';
              const estado = row[4] ? String(row[4]).trim() : '';
              const tempoDeParada = row[5] ? String(row[5]).trim() : '';
              const quantidade = row[6] ? String(row[6]).trim() : '';
              const tipoOperacao = row[7] ? String(row[7]).trim() : '';

              const isPositionCoordinate = isCoordinate(endereco);

              // Validar se o tipo de operação é válido
              const validOperationTypes = Object.values(EOperationType);
              const operationType = validOperationTypes.includes(tipoOperacao as EOperationType)
                ? tipoOperacao as EOperationType
                : EOperationType.WAYPOINT; // Fallback para ponto de passagem

              return {
                zipCode: cep,
                city: cidade,
                company: empresa,
                position: isPositionCoordinate ? endereco : '',
                address: isPositionCoordinate ? '' : endereco,
                state: estado,
                quantity: Number(quantidade) || 0,
                stopTime: formatStopTime(tempoDeParada),
                operationType: operationType,
              }
            })
            setCsvJson(dataJsonXLSTemp);
          } else {
            setIsValid(false);
            setError('Número de colunas inválido. Use o modelo correto.');
            setCsvJson(null);
          }
        } catch (error) {
          console.error('Erro ao processar arquivo Excel:', error);
          setIsValid(false);
          setError('Erro ao ler arquivo Excel. Verifique se o arquivo está correto.');
          setCsvJson(null);
        }
      };
      reader.readAsArrayBuffer(file);
    } else if (fileExtension === 'csv') {
      // Leitura de arquivo CSV (mantém compatibilidade)
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result as string;
        const lines = text.trim().split('\n');
        const rows = rowSlice(lines);

        // Validação do número de colunas
        const isColumnCountValid = rows.every(row => row.length >= expectedColumnCount);

        if (isColumnCountValid) {
          setIsValid(true);
          setError(null);
          const dataJsonXLSTemp: ICsvData[] = rows.map(row => {
            // Lê pela sequência das colunas (índices fixos)
            const empresa = row[0] || '';
            const endereco = row[1] || '';
            const cep = row[2] || '';
            const cidade = row[3] || '';
            const estado = row[4] || '';
            const tempoDeParada = row[5] || '';
            const quantidade = row[6] || '';
            const tipoOperacao = row[7] || '';

            const isPositionCoordinate = isCoordinate(endereco);

            // Validar se o tipo de operação é válido
            const validOperationTypes = Object.values(EOperationType);
            const operationType = validOperationTypes.includes(tipoOperacao as EOperationType)
              ? tipoOperacao as EOperationType
              : EOperationType.WAYPOINT; // Fallback para ponto de passagem

            return {
              zipCode: cep,
              city: cidade,
              company: empresa,
              position: isPositionCoordinate ? endereco : '',
              address: isPositionCoordinate ? '' : endereco,
              state: estado,
              quantity: Number(quantidade) || 0,
              stopTime: formatStopTime(tempoDeParada),
              operationType: operationType,
            }
          })
          setCsvJson(dataJsonXLSTemp);
        } else {
          setIsValid(false);
          setError('Número de colunas inválido. Use o modelo correto.');
          setCsvJson(null);
        }
      };
      reader.readAsText(file);
    } else {
      // Arquivo não suportado
      setIsValid(false);
      setError('Formato de arquivo não suportado. Use arquivos .xlsx, .xls ou .csv');
      setCsvJson(null);
    }
  };

  // Chama a leitura do arquivo sempre que um novo arquivo for selecionado
  React.useEffect(() => {
    if (selectedFile) {
      readFile(selectedFile);
    } else {
      setIsValid(false);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile]);

  return (
    <div
      className={`
        display-flex
        flex-column
        gap-24
        border-width-1
        border-style-solid
        rounded
        justify-content-center
        align-items-center
        shadow-default
        border-color-gray
        margin-10
        transition-all
        duration-200
        cursor-pointer
        hover-bg-lightest
        hover-bg-lighter
        ${isActive ? "bg-highlight-lightest border-color-primary" : ""}
        ${dragActive ? "border-color-primary bg-highlight-lightest" : ""}
      `}
      onClick={handleButtonClick}
      onDragEnter={handleDrag}
      onDragOver={handleDrag}
      onDragLeave={handleDrag}
      onDrop={handleDrop}
      style={{ position: "relative" }}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.xls,.xlsx"
        style={{ display: "none" }}
        onChange={handleChange}
      />
      <div
        className={`
          border
          ${selectedFile ? "border-color-primary" : "border-color-gray"}
          border-width-3
          border-style-solid
          display-flex
          justify-content-center
          align-items-center
          margin-top-20
          width-50
          height-50
        `}
      >
        <span className={`${icon} text-size-h1 ${selectedFile ? "text-color-primary" : "text-color-gray"}`} />
      </div>
      <div className={`display-flex flex-column margin-y-20 justify-content-center align-items-center`}>
        <span className="text-size-16 line-height-20 text-normal margin-x-20 height-20">{text}</span>
        <span className={`text-size-13 text-normal text-color-status-resting margin-x-20 margin-top-5 height-20`}>{text2}</span>
        {selectedFile && (
          <span className="text-size-13 text-color-primary margin-top-10">{selectedFile.name}</span>
        )}
      </div>
      <div className="display-flex flex-row gap-10 margin-bottom-20">
        {textBtnDownload && (
          <button
            type="button"
            className="btn btn-default btn-lg width-150"
            disabled={downloading}
            onClick={async (e) => {
              e.stopPropagation();
              if (downloading) return;
              setDownloading(true);
              try {
                await new Promise(resolve => setTimeout(resolve, 2000));
                await handleDownloadXlsExample();
                await new Promise(resolve => setTimeout(resolve, 1000));
              } finally {
                setDownloading(false);
              }
            }}
          >
            <div className="display-flex align-items-center gap-8 width-100pct">
              {downloading ? (
                <>
                  <span className="rioglyph rioglyph-refresh spinning text-size-150pct margin-right-10" />
                  <span className="text-capitalize">Aguarde...</span>
                </>
              ) : (
                <>
                  <span className="rioglyph rioglyph-checkboxes text-size-150pct margin-right-10" />
                  <span className="text-capitalize">{textBtnDownload}</span>
                </>
              )}
            </div>
          </button>
        )}
        {textBtnNext && onNext && (
          <button
            type="button"
            className="btn btn-primary btn-lg"
            onClick={(e) => {
              e.stopPropagation();
              if (csvJson) onNext(csvJson);
            }}
            disabled={!selectedFile || !isValid}
          >
            <span className="text-capitalize">{textBtnNext ?? "Continuar"}</span>
          </button>
        )}
      </div>
      {error && (
        <div className="text-color-status-error text-size-13 margin-bottom-10">{error}</div>
      )}

      {/* CSS para animação de loading */}
      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default TripCardAddFile; 